import * as React from "react";
import { cn } from "@/lib/utils";

export interface EmptyStateProps {
  /** Icon element, e.g. a lucide/feather icon. Sized by the wrapper. */
  icon?: React.ReactNode;
  /** Sentence case, no terminal period, e.g. "No campaigns yet". */
  title: string;
  /** One line naming the action that fills the screen. */
  body?: string;
  /** Optional CTA, typically a <Button>. */
  action?: React.ReactNode;
  /** Render the bordered card shell. Off when already inside a card/table. */
  bordered?: boolean;
  className?: string;
}

/**
 * The one empty-state pattern: icon → title → one-line body → optional action.
 * Copy rule: sentence case, no terminal period, name the action that fills
 * the screen.
 */
export function EmptyState({
  icon,
  title,
  body,
  action,
  bordered = true,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-2 px-6 py-12 text-center",
        bordered && "rounded-lg border border-border bg-card",
        className,
      )}
    >
      {icon && (
        <div className="mb-1 flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground [&>svg]:h-5 [&>svg]:w-5">
          {icon}
        </div>
      )}
      <p className="text-sm font-medium text-foreground">{title}</p>
      {body && <p className="text-sm text-muted-foreground">{body}</p>}
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}
